import { getMarketingTranslations, interpolateMarketingTranslation } from "../../marketing-content/marketingTranslations";
import {
  MAX_PDF_PAGES_ALLOWED_FOR_FREE,
  MAX_VIDEO_MIN_ALLOWED_FOR_FREE,
  NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT,
  NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED,
  NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED,
  NUM_OF_FREE_MONTHLY_LIVE_RECORDINGS_ALLOWED,
  NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { MarketingPageLanguage } from "../../marketing-content/marketingContentLoader";

// Copy for the public pricing page (jungleai.com/pricing and /es/pricing).
//
// The page must say exactly what the app's paywall says, so:
//  - what each paid plan includes is the paywall's own wording. The marketing site does not load the app's
//    translation files, so the strings are copied here and `marketingPricingCopy.test.ts` fails when one of
//    them drifts from `src/i18n/en.json` / `es-ES.json` (PAYWALL_TRANSLATION_KEY_BY_PRICING_COPY_FIELD);
//  - the Free plan's limits are built from the same constants the app enforces;
//  - prices are not here at all: the page fetches them live, as the paywall does.

/** Pricing-copy fields whose text is the paywall's, and the translation key each one mirrors. */
export const PAYWALL_TRANSLATION_KEY_BY_PRICING_COPY_FIELD = {
  payMonthly: "paywall.twoStep.step2.toggle_monthly",
  payYearly: "paywall.twoStep.step2.toggle_annual",
  perMonth: "paywall.twoStep.plan_card.price_per_month_label",
  billedYearly: "paywall.twoStep.plan_card.price_billed_yearly",
  priceLoading: "paywall.twoStep.plan_card.price_loading",
  proIncludes: "paywall.twoStep.plan_card.pro_includes",
  proUnlimitedGenerations: "paywall.twoStep.plan_card.pro_unlimited_generations",
  proThreeTimesMoreQuestions: "paywall.twoStep.plan_card.pro_3x_more_questions",
  proUnlimitedAdvancedQuestionTypes: "paywall.twoStep.plan_card.pro_unlimited_advanced_question_types",
  proUnlimitedAiExplanations: "paywall.twoStep.plan_card.pro_unlimited_ai_explanations",
  proExportCardsToAnki: "paywall.twoStep.plan_card.pro_export_cards_to_anki",
  proUnlimitedLectureRecordings: "paywall.twoStep.plan_card.pro_unlimited_lecture_recordings",
  eliteEverythingInPro: "paywall.twoStep.plan_card.elite_everything_in_pro",
  // The Elite-first paywall is the one every visitor sees, so its wording wins where the two flows differ.
  eliteQualityBoost: "paywall.eliteFirst.bullet_quality_boost",
  eliteExamTemplates: "paywall.eliteFirst.bullet_exam_templates",
  eliteAutoImageOcclusion: "paywall.twoStep.plan_card.elite_auto_image_occlusion",
  eliteAiVoiceTutorCalls: "paywall.twoStep.plan_card.elite_ai_voice_mode",
  eliteDailyReviewSize: "paywall.twoStep.plan_card.elite_daily_review_size",
} as const;

type PaywallPricingCopyField = keyof typeof PAYWALL_TRANSLATION_KEY_BY_PRICING_COPY_FIELD;

export type MarketingPricingCopy = Record<PaywallPricingCopyField, string> & {
  annualDiscountNote: (discountPercentage: number) => string;
  fullAnnualPriceNote: (fullAnnualPrice: string) => string;
  freePlanName: string;
  proPlanName: string;
  elitePlanName: string;
  freePlanFeatures: string[];
  freePlanCallToAction: string;
  proPlanCallToAction: string;
  elitePlanCallToAction: string;
  localPricingNote: string;
};

export function getMarketingPricingCopy(lang: MarketingPageLanguage): MarketingPricingCopy {
  const copy = getMarketingTranslations(lang).pricing;
  const limits = { NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED, MAX_PDF_PAGES_ALLOWED_FOR_FREE, MAX_VIDEO_MIN_ALLOWED_FOR_FREE, NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED, NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED, NUM_OF_FREE_MONTHLY_LIVE_RECORDINGS_ALLOWED, NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT };
  return { ...copy,
    annualDiscountNote: discountPercentage => interpolateMarketingTranslation(copy.annualDiscountNote, { discountPercentage }),
    fullAnnualPriceNote: fullAnnualPrice => interpolateMarketingTranslation(copy.fullAnnualPriceNote, { fullAnnualPrice }),
    freePlanFeatures: copy.freePlanFeatures.map(text => interpolateMarketingTranslation(text, limits)),
  };
}
export const MARKETING_PRICING_COPY = { en: getMarketingPricingCopy("en"), es: getMarketingPricingCopy("es") };
